import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Eye,
  Edit3,
  Trash2,
  Pill,
  X,
  AlertTriangle,
  Layers,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../context/AuthContext';

const categoryColors = {
  Tablet:  'bg-blue-100 text-blue-700',
  Syrup:   'bg-purple-100 text-purple-700',
  Inhaler: 'bg-amber-100 text-amber-700',
  Capsule: 'bg-emerald-100 text-emerald-700',
  Drop:    'bg-cyan-100 text-cyan-700',
  Cream:   'bg-rose-100 text-rose-700',
  Injection: 'bg-red-100 text-red-700',
};

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

const COMMON_UNIT_NAMES = [
  'Box',
  'Pack',
  'Strip',
  'Tablet',
  'Capsule',
  'Bottle',
  'Syrup',
  'Piece',
  'Tube',
  'Sachet',
  'Ampoule',
  'Vial',
  'Drop',
  'Inhaler',
  'Carton',
  'Blister',
];

const PAGE_SIZE = 50;

function getDaysLeft(expiryDate) {
  if (!expiryDate) return 999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate);
  exp.setHours(0, 0, 0, 0);
  const diff = exp.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getExpiryColor(days) {
  if (days <= 0) return 'bg-red-100 text-red-800 border-red-300 font-bold';
  if (days <= 30) return 'bg-red-50 text-red-700 border-red-200';
  if (days <= 90) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function getExpiryBadgeColor(days) {
  if (days <= 0) return 'text-red-700 font-bold';
  if (days <= 30) return 'text-red-500';
  if (days <= 90) return 'text-amber-500';
  return 'text-gray-500';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
}

function formatStockDisplay(totalBaseStock, units = []) {
  if (!units || units.length <= 1) {
    const baseName = units?.[0]?.unit_name || 'units';
    return `${totalBaseStock} ${baseName}`;
  }

  const topUnit = units[0];
  const baseUnit = units[units.length - 1];
  const topFactor = topUnit.conversion_factor || 1;

  if (topFactor <= 1) {
    return `${totalBaseStock} ${baseUnit.unit_name || 'units'}`;
  }

  const fullBoxes = Math.floor(totalBaseStock / topFactor);
  const remainingBase = totalBaseStock % topFactor;

  if (remainingBase === 0) {
    return `${totalBaseStock} ${baseUnit.unit_name}s (${fullBoxes} ${topUnit.unit_name}${fullBoxes !== 1 ? 's' : ''})`;
  }

  return `${totalBaseStock} ${baseUnit.unit_name}s (${fullBoxes} ${topUnit.unit_name}${fullBoxes !== 1 ? 's' : ''} + ${remainingBase} ${baseUnit.unit_name}s)`;
}

export default function Medicines() {
  const { user } = useAuth();
  const isCashier = user?.role === 'cashier';
  const navigate = useNavigate();
  const [medicines, setMedicines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMed, setViewMed] = useState(null);
  const [editMed, setEditMed] = useState(null);
  const [deleteMed, setDeleteMed] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editUnits, setEditUnits] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await api.get('/categories');
        setCategories(Array.isArray(cats) ? cats : (cats?.categories || []));
      } catch (err) {
        toast.error(err.message || 'Failed to load categories');
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
        if (search.trim()) params.set('search', search.trim());
        const medRes = await api.get(`/medicines?${params.toString()}`);
        if (cancelled) return;

        const list = Array.isArray(medRes) ? medRes : (medRes?.medicines || []);
        const pagination = medRes?.pagination;
        const resultPages = pagination?.pages ?? Math.max(1, Math.ceil(list.length / PAGE_SIZE));

        // A deletion can make the current last page disappear.
        if (pagination?.pages && page > pagination.pages) {
          setPage(pagination.pages);
          return;
        }

        setMedicines(list);
        setTotal(pagination?.total ?? list.length);
        setPages(resultPages);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load medicines');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [page, search, refreshKey]);

  const reloadMedicines = () => setRefreshKey((key) => key + 1);

  const getStockInfo = (med) => ({
    stock: med.total_stock || 0,
    expiry: med.nearest_expiry || null,
    daysLeft: med.nearest_expiry ? getDaysLeft(med.nearest_expiry) : 999,
    costPrice: med.avg_cost_price || 0,
  });

  const openAdd = () => {
    navigate('/medicines/new');
  };

  const openEdit = (med) => {
    navigate(`/medicines/${med.id}/edit`);
  };

  const handleEditUnitChange = (idx, field, value) => {
    setEditUnits(prev => {
      const copy = prev.map((u, i) => i === idx ? { ...u, [field]: value } : { ...u });
      if (field === 'child_count') {
        for (let i = idx; i < copy.length - 1; i++) {
          const p = copy[i];
          const c = copy[i + 1];
          const r = Math.max(1, parseInt(p.child_count, 10) || 1);
          c.cost_price = (parseFloat(p.cost_price) / r).toFixed(2);
          c.selling_price = (parseFloat(p.selling_price) / r).toFixed(2);
        }
      }
      return copy;
    });
  };

  const addEditUnitLevel = () => {
    setEditUnits(prev => {
      const last = prev[prev.length - 1];
      const newU = {
        unit_name: 'Piece',
        child_count: 1,
        cost_price: last ? (parseFloat(last.cost_price) / 10).toFixed(2) : 0,
        selling_price: last ? (parseFloat(last.selling_price) / 10).toFixed(2) : 0,
      };
      if (last) {
        last.child_count = 10;
      }
      return [...prev, newU];
    });
  };

  const removeEditUnitLevel = (idx) => {
    if (editUnits.length <= 1) {
      toast.error('At least one unit level is required.');
      return;
    }
    setEditUnits(prev => {
      const copy = prev.filter((_, i) => i !== idx);
      if (copy.length > 0) {
        copy[copy.length - 1].child_count = 1;
      }
      return copy;
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const topUnit = editUnits[0] || {};
      const baseUnit = editUnits[editUnits.length - 1] || {};

      const formattedUnits = editUnits.map((u, idx) => ({
        unit_name: u.unit_name.trim() || `Level ${idx + 1}`,
        child_count: idx === editUnits.length - 1 ? 1 : Math.max(1, parseInt(u.child_count, 10) || 1),
        cost_price: Math.max(0, parseFloat(u.cost_price) || 0),
        selling_price: Math.max(0, parseFloat(u.selling_price) || 0),
        level: idx + 1,
      }));

      await api.put(`/medicines/${editForm.id}`, {
        name: editForm.name,
        generic_name: editForm.generic_name,
        category_id: editForm.category_id ? parseInt(editForm.category_id, 10) : null,
        supplier_id: editForm.supplier_id ? parseInt(editForm.supplier_id, 10) : null,
        manufacturer: editForm.manufacturer,
        barcode: editForm.barcode,
        sku: editForm.sku,
        selling_price: parseFloat(topUnit.selling_price) || parseFloat(editForm.selling_price) || 0,
        unit: topUnit.unit_name || editForm.unit || 'pcs',
        reorder_level: parseInt(editForm.reorder_level, 10) || 0,
        description: editForm.description,
        units: formattedUnits,
      });

      toast.success('Medicine and unit hierarchy updated');
      setEditMed(null);
      reloadMedicines();
    } catch (err) {
      toast.error(err.message || 'Failed to update medicine');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/medicines/${deleteMed.id}`);
      toast.success('Medicine deleted');
      setDeleteMed(null);
      reloadMedicines();
    } catch (err) {
      toast.error(err.message || 'Failed to delete medicine');
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Top Bar ── */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, generic, barcode..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-24 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
          <button type="button" onClick={reloadMedicines} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200">
            Find
          </button>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          New Medicine
        </button>
      </div>

      {/* ── Table ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-gray-900">Product Catalog</h2>
          </div>
          <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
            {total.toLocaleString()} Medicines
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Medicine & Packaging</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Category</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Manufacturer</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Multi-Unit Pricing</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Stock Level</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Nearest Expiry</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {medicines.map((med, idx) => {
                const info = getStockInfo(med);
                const catName = categories.find((c) => c.id === med.category_id)?.name || '';
                const units = med.units || [];
                const topU = units[0];

                return (
                  <tr key={med.id} className="group transition-colors hover:bg-gray-50/50">
                    <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className={clsx('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold', avatarColors[idx % avatarColors.length])}>
                          {(med.name || 'M').charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{med.name}</p>
                          <p className="text-xs text-gray-400">{med.generic_name || ''}</p>
                          {units.length > 1 && (
                            <div className="mt-1 flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded w-fit">
                              <Layers className="h-3 w-3" />
                              <span>1 {topU?.unit_name} = {topU?.conversion_factor} {units[units.length - 1]?.unit_name || 'units'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {catName && (
                        <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold', categoryColors[catName] || 'bg-gray-100 text-gray-700')}>
                          {catName}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{med.manufacturer || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-bold text-gray-900">
                        Rs. {(parseFloat(topU?.selling_price || med.selling_price) || 0).toFixed(2)}
                        <span className="text-[11px] text-gray-500 font-normal ml-1">/ {topU?.unit_name || med.unit || 'pcs'}</span>
                      </p>
                      {units.length > 1 && (
                        <div className="mt-0.5 space-y-0.5 text-[10px] text-gray-500">
                          {units.slice(1).map(u => (
                            <p key={u.unit_name}>
                              {u.unit_name}: <strong className="text-gray-700">Rs. {(parseFloat(u.selling_price) || 0).toFixed(2)}</strong>
                            </p>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold', info.stock <= (med.reorder_level || 0) ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')}>
                        {formatStockDisplay(info.stock, units)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {info.expiry ? (
                        <>
                          <span className={clsx('inline-block rounded-md border px-2.5 py-1 text-[11px] font-bold', getExpiryColor(info.daysLeft))}>
                            {formatDate(info.expiry)}
                          </span>
                          <p className={clsx('mt-1 text-[11px] font-medium', getExpiryBadgeColor(info.daysLeft))}>
                            {info.daysLeft < 0 ? `Expired ${Math.abs(info.daysLeft)}d ago` : info.daysLeft === 0 ? 'Expires today (0d)' : `${info.daysLeft} days left`}
                          </p>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No batch</span>
                      )}
                    </td>
                    <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setViewMed(med)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"><Eye className="h-4 w-4" /></button>
                        {!isCashier && <button onClick={() => openEdit(med)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><Edit3 className="h-4 w-4" /></button>}
                        {!isCashier && <button onClick={() => setDeleteMed(med)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && medicines.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Pill className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No medicines found. Click "New Medicine" to add one.</p>
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading medicines...
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            <p className="text-sm text-gray-500">
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page === 1 || loading}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm font-medium text-gray-600">Page {page} of {pages}</span>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(pages, current + 1))}
                disabled={page >= pages || loading}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══════ VIEW MODAL ══════ */}
      {viewMed &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewMed(null)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <Pill className="h-5 w-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-gray-900">Medicine Specifications</h3>
                </div>
                <button onClick={() => setViewMed(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              {(() => {
                const info = getStockInfo(viewMed);
                const catName = categories.find((c) => c.id === viewMed.category_id)?.name || '-';
                const units = viewMed.units || [];
                return (
                  <div className="space-y-3.5 text-sm">
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Name</span><span className="font-semibold text-gray-900">{viewMed.name}</span></div>
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Generic</span><span className="text-gray-900">{viewMed.generic_name || '-'}</span></div>
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Category</span><span className="text-gray-900">{catName}</span></div>
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Barcode</span><span className="text-gray-900">{viewMed.barcode || '-'}</span></div>
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">SKU</span><span className="text-gray-900">{viewMed.sku || '-'}</span></div>
                    <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Total Stock</span><span className="font-bold text-emerald-700">{formatStockDisplay(info.stock, units)}</span></div>

                    {/* Multi-Unit Pricing Matrix Table */}
                    {units.length > 0 && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider">
                          <Layers className="h-4 w-4 text-emerald-600" />
                          <span>Unit Packaging & Pricing</span>
                        </div>
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-gray-200 text-gray-500">
                              <th className="pb-1">Unit</th>
                              <th className="pb-1 text-center">Multiplier</th>
                              <th className="pb-1 text-right">Cost (Rs.)</th>
                              <th className="pb-1 text-right">Selling (Rs.)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {units.map((u) => (
                              <tr key={u.id || u.unit_name} className="text-gray-700">
                                <td className="py-1 font-semibold">{u.unit_name}</td>
                                <td className="py-1 text-center">{u.conversion_factor}x</td>
                                <td className="py-1 text-right">Rs. {(parseFloat(u.cost_price) || 0).toFixed(2)}</td>
                                <td className="py-1 text-right font-bold text-emerald-700">Rs. {(parseFloat(u.selling_price) || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>,
          document.body
        )}

      {/* ══════ EDIT MODAL ══════ */}
      {editMed &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditMed(null)} />
            <div className="relative w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <Edit3 className="h-5 w-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-gray-900">Edit Medicine & Unit Packaging</h3>
                </div>
                <button onClick={() => setEditMed(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>

              <form onSubmit={saveEdit} className="max-h-[75vh] overflow-y-auto px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Medicine Name</label>
                    <input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Generic Formula</label>
                    <input value={editForm.generic_name || ''} onChange={(e) => setEditForm({ ...editForm, generic_name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Manufacturer</label>
                    <input value={editForm.manufacturer || ''} onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Category</label>
                    <SearchableSelect
                      value={editForm.category_id ? String(editForm.category_id) : ''}
                      onChange={(val) => setEditForm({ ...editForm, category_id: val })}
                      options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select category..."
                      searchPlaceholder="Search category..."
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Barcode</label>
                    <input value={editForm.barcode || ''} onChange={(e) => setEditForm({ ...editForm, barcode: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                </div>

                {/* Unit Hierarchy Section in Edit */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3.5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800 uppercase tracking-wider">
                      <Layers className="h-4 w-4 text-emerald-600" />
                      <span>Unit Hierarchy & Pricing Matrix</span>
                    </div>
                    <button
                      type="button"
                      onClick={addEditUnitLevel}
                      className="flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-800"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Level
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                          <th className="py-2 px-2.5 text-center w-12">Level</th>
                          <th className="py-2 px-2.5">Unit Name</th>
                          <th className="py-2 px-2.5">Contains Next</th>
                          <th className="py-2 px-2.5">Cost (Rs.)</th>
                          <th className="py-2 px-2.5">Selling (Rs.)</th>
                          <th className="py-2 px-1 text-center w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {editUnits.map((u, idx) => {
                          const isTop = idx === 0;
                          const isBase = idx === editUnits.length - 1;
                          const nextUnitName = !isBase ? editUnits[idx + 1].unit_name || 'items' : '';

                          return (
                            <tr key={idx}>
                              <td className="py-2 px-2.5 text-center font-bold text-gray-500">
                                {idx + 1}
                              </td>
                              <td className="py-2 px-2.5 min-w-[140px]">
                                <SearchableSelect
                                  value={u.unit_name}
                                  onChange={(val) => handleEditUnitChange(idx, 'unit_name', val)}
                                  options={COMMON_UNIT_NAMES}
                                  placeholder={isTop ? 'e.g. Box' : isBase ? 'e.g. Tablet' : 'e.g. Strip'}
                                  searchPlaceholder="Search or type unit..."
                                  creatable={true}
                                  size="sm"
                                  buttonClassName="bg-white border-gray-200 py-1 text-xs font-semibold"
                                />
                              </td>
                              <td className="py-2 px-2.5">
                                {!isBase ? (
                                  <div className="flex items-center gap-1">
                                    <span>1 = </span>
                                    <input
                                      type="number"
                                      min="1"
                                      value={u.child_count}
                                      onChange={(e) => handleEditUnitChange(idx, 'child_count', e.target.value)}
                                      className="w-12 rounded border border-gray-200 bg-white px-1.5 py-1 text-center text-xs font-bold"
                                    />
                                    <span className="text-[10px] text-gray-500 truncate">{nextUnitName}s</span>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-blue-600 font-bold">Base Unit</span>
                                )}
                              </td>
                              <td className="py-2 px-2.5">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={u.cost_price}
                                  onChange={(e) => handleEditUnitChange(idx, 'cost_price', e.target.value)}
                                  className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-semibold"
                                />
                              </td>
                              <td className="py-2 px-2.5">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={u.selling_price}
                                  onChange={(e) => handleEditUnitChange(idx, 'selling_price', e.target.value)}
                                  className="w-20 rounded border border-emerald-300 bg-emerald-50/30 px-2 py-1 text-xs font-bold text-emerald-950"
                                  required
                                />
                              </td>
                              <td className="py-2 px-1 text-center">
                                {editUnits.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeEditUnitLevel(idx)}
                                    className="text-gray-400 hover:text-red-500 p-1"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button type="button" onClick={() => setEditMed(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ══════ DELETE CONFIRM MODAL ══════ */}
      {deleteMed &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteMed(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Medicine?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteMed.name}</strong>? This will also remove all its batches.</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteMed(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
